import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { StorageService } from './storage.service';

export enum UserRole {
  SCA_SUPER_ADMIN        = 'SCA_SUPER_ADMIN',
  SCA_IES_ADMIN          = 'SCA_IES_ADMIN',
  SCA_ETABLISSEMENT_ADMIN = 'SCA_ETABLISSEMENT_ADMIN',
}

/** Matrice déclarative : quelles pages chaque rôle peut voir */
export const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  'dashboard/users':         [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'dashboard/clients':       [UserRole.SCA_SUPER_ADMIN],
  'dashboard/groups':        [UserRole.SCA_SUPER_ADMIN],
  'dashboard/roles':         [UserRole.SCA_SUPER_ADMIN],
  'dashboard/journalisation':[UserRole.SCA_SUPER_ADMIN],
  'dashboard/admin-audit':   [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'dashboard/historiques':   [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
};

/** Matrice déclarative : quelles actions chaque rôle peut effectuer */
export const ACTION_PERMISSIONS: Record<string, UserRole[]> = {
  // Gestion utilisateurs
  'users.create':            [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'users.edit':              [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'users.delete':            [UserRole.SCA_SUPER_ADMIN],
  'users.toggle_status':     [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'users.manage_groups':     [UserRole.SCA_SUPER_ADMIN],
  'users.manage_roles':      [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'users.reset_password':    [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
  'users.send_verification': [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],

  // Gestion clients/applications
  'clients.create':          [UserRole.SCA_SUPER_ADMIN],
  'clients.edit':            [UserRole.SCA_SUPER_ADMIN],
  'clients.delete':          [UserRole.SCA_SUPER_ADMIN],

  // Gestion rôles
  'roles.create':            [UserRole.SCA_SUPER_ADMIN],
  'roles.edit':              [UserRole.SCA_SUPER_ADMIN],
  'roles.delete':            [UserRole.SCA_SUPER_ADMIN],

  // Accès global à l'application
  'app.access':              [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN],
};

export interface UserPermissionContext {
  roles: UserRole[];
  isSuperAdmin: boolean;
  isIesAdmin: boolean;
  isEtablissementAdmin: boolean;
  canAccessApp: boolean;
}

const KEYCLOAK_CLIENT_ID = 'habilitation';

@Injectable({ providedIn: 'root' })
export class PermissionService {

  private storage = inject(StorageService);

  /** Contexte réactif : les composants s'y abonnent */
  private context$ = new BehaviorSubject<UserPermissionContext>(this.emptyContext());

  /** Exposé en lecture seule */
  readonly context: Observable<UserPermissionContext> = this.context$.asObservable();

  // ── Initialisation ──────────────────────────────────────────────────────────

  /**
   * À appeler dès qu'un token est disponible (après login Keycloak).
   * Peut être appelé plusieurs fois (refresh token).
   */
  initFromToken(token: string): void {
    const roles = this.extractRolesFromToken(token);
    const ctx   = this.buildContext(roles);
    this.context$.next(ctx);
  }

  /**
   * Relit le token depuis le storage (utile au démarrage de l'app).
   */
  initFromStorage(): void {
    const token = this.storage.getItem('access_token');
    if (token) {
      this.initFromToken(token);
    }
  }

  /** Réinitialise le contexte (logout) */
  reset(): void {
    this.context$.next(this.emptyContext());
  }

  // ── API synchrone (utile dans les guards, templates) ────────────────────────

  /** Snapshot du contexte courant */
  get snapshot(): UserPermissionContext {
    return this.context$.getValue();
  }

  /** L'utilisateur a-t-il AU MOINS UN des rôles fournis ? */
  hasAnyRole(...roles: UserRole[]): boolean {
    const current = this.snapshot.roles;
    return roles.some(r => current.includes(r));
  }

  /** L'utilisateur a-t-il TOUS les rôles fournis ? */
  hasAllRoles(...roles: UserRole[]): boolean {
    const current = this.snapshot.roles;
    return roles.every(r => current.includes(r));
  }

  /** L'utilisateur peut-il effectuer une action nommée ? */
  can(action: string): boolean {
    // SUPER_ADMIN court-circuite tout
    if (this.snapshot.isSuperAdmin) return true;

    const allowed = ACTION_PERMISSIONS[action];
    if (!allowed) return false;
    return this.hasAnyRole(...allowed);
  }

  /** L'utilisateur peut-il accéder à une route ? */
  canAccessRoute(routePath: string): boolean {
    if (this.snapshot.isSuperAdmin) return true;

    const allowed = ROUTE_PERMISSIONS[routePath];
    if (!allowed) return true; // Route non listée = ouverte
    return this.hasAnyRole(...allowed);
  }

  /** Stream réactif de `can()` — pour les templates avec `async` pipe */
  can$(action: string): Observable<boolean> {
    return this.context.pipe(map(() => this.can(action)));
  }

  // ── Helpers privés ──────────────────────────────────────────────────────────

  private extractRolesFromToken(token: string): UserRole[] {
    try {
      const base64Url = token.split('.')[1];
      const base64    = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload   = JSON.parse(
        decodeURIComponent(
          atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join('')
        )
      );

      // 1. Rôles du client Keycloak (resource_access.habilitation.roles)
      const clientRoles: string[] =
        payload?.resource_access?.[KEYCLOAK_CLIENT_ID]?.roles ?? [];

      // 2. Rôles realm en fallback si besoin
      // const realmRoles: string[] = payload?.realm_access?.roles ?? [];

      // Ne garder que les rôles connus de l'enum
      return clientRoles.filter((r): r is UserRole =>
        Object.values(UserRole).includes(r as UserRole)
      );
    } catch {
      return [];
    }
  }

  private buildContext(roles: UserRole[]): UserPermissionContext {
    return {
      roles,
      isSuperAdmin:         roles.includes(UserRole.SCA_SUPER_ADMIN),
      isIesAdmin:           roles.includes(UserRole.SCA_IES_ADMIN),
      isEtablissementAdmin: roles.includes(UserRole.SCA_ETABLISSEMENT_ADMIN),
      canAccessApp:         roles.some(r =>
        [UserRole.SCA_SUPER_ADMIN, UserRole.SCA_IES_ADMIN].includes(r)
      ),
    };
  }

  private emptyContext(): UserPermissionContext {
    return {
      roles:                [],
      isSuperAdmin:         false,
      isIesAdmin:           false,
      isEtablissementAdmin: false,
      canAccessApp:         false,
    };
  }
}